import React, { useState, useEffect } from 'react';
import { Lock, Key, Shield, User, Bell, Mail, MessageSquare, Database, Info } from 'lucide-react';
import SubNav from '../components/subNav';
import SEO from '../components/SEO';
import { useNavigate } from 'react-router-dom';
import { baseUrl } from '../config';

// Utility for responsive classes
const screens = {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
};

// Custom Switch Component
const Switch = ({ checked, onCheckedChange, disabled = false }) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onCheckedChange()}
        className={`relative inline-flex h-5 w-10 sm:h-6 sm:w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${checked ? 'bg-blue-600' : 'bg-gray-300'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
        <span
            className={`inline-block h-4 w-4 sm:h-5 sm:w-5 transform rounded-full bg-white transition-transform duration-200 ease-in-out shadow-md ${checked ? 'translate-x-5 sm:translate-x-6' : 'translate-x-1'
                }`}
        />
    </button>
);

// Custom Tooltip Component
const Tooltip = ({ children, content, position = "top" }) => {
    return (
        <div className="relative group inline-block">
            {children}
            <div className={`absolute z-10 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap
        ${position === 'top' ? '-top-8 left-1/2 transform -translate-x-1/2' : ''}
        ${position === 'bottom' ? '-bottom-8 left-1/2 transform -translate-x-1/2' : ''}
        ${position === 'left' ? 'top-1/2 -left-2 transform -translate-y-1/2 -translate-x-full' : ''}
        ${position === 'right' ? 'top-1/2 -right-2 transform -translate-y-1/2 translate-x-full' : ''}
      `}>
                {content}
                <div className={`absolute w-2 h-2 bg-gray-800 transform rotate-45
          ${position === 'top' ? 'bottom-0 left-1/2 -translate-x-1/2 translate-y-1' : ''}
          ${position === 'bottom' ? 'top-0 left-1/2 -translate-x-1/2 -translate-y-1' : ''}
          ${position === 'left' ? 'top-1/2 right-0 -translate-y-1/2 translate-x-1' : ''}
          ${position === 'right' ? 'top-1/2 left-0 -translate-y-1/2 -translate-x-1' : ''}
        `}></div>
            </div>
        </div>
    );
};

// Custom Card Component
const Card = ({ children, className = '' }) => (
    <div className={`bg-white rounded-lg shadow-lg overflow-hidden ${className}`}>
        {children}
    </div>
);

// Custom Button Component
const Button = ({
    children,
    variant = 'primary',
    type = 'button',
    onClick,
    disabled = false,
    className = '',
    fullWidth = false
}) => (
    <button
        type={type}
        onClick={onClick}
        disabled={disabled}
        className={`px-4 py-2 rounded-md font-medium transition-colors text-sm sm:text-base
            ${fullWidth ? 'w-full' : ''}
            ${variant === 'primary'
                ? 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'
                : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 active:bg-gray-100'
            } 
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''} 
            ${className}`}
    >
        {children}
    </button>
);

const SecuritySettings = () => {
    const [formErrors, setFormErrors] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const navigate = useNavigate();
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });

    const [showPasswordForm, setShowPasswordForm] = useState(false);
    const [passwordChange, setPasswordChange] = useState(0);

    const validatePassword = (password) => {
        const regex = /^(?=.*[A-Z])(?=.*[!@#$%^&*])(?=.*\d)[A-Za-z\d!@#$%^&*]{10,}$/;
        return regex.test(password);
    };

    const handlePasswordChange = async (e) => {
        let current = e.target.name;

        setPasswordForm(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));

        if (current === "currentPassword") {
            setFormErrors(prevErrors => ({ ...prevErrors, currentPassword: "" }));
            const userId = await fetch(`${baseUrl}/getUserIDByToken`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                },
            });
            const id = await userId.json();
            try {
                const response = await fetch(`${baseUrl}/checkPassword`, {
                    headers: {
                        user_id: id.user_id,
                        password: e.target.value
                    },
                });
                const data = await response.json();
                if (!data || data.detail) {
                    setFormErrors(prevErrors => ({ ...prevErrors, currentPassword: "Password is incorrect." }));
                }
            } catch (error) {
            }
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            alert("New passwords don't match!");
            return;
        }
        if (!validatePassword(passwordForm.newPassword)) {
            setFormErrors(prevErrors => ({ ...prevErrors, newPassword: "Password must be at least 10 characters long, include 1 capital letter, 1 special character, and 1 numerical digit." }));
        } else {
            setFormErrors(prevErrors => ({ ...prevErrors, newPassword: "" }));
        }
        if (!validatePassword(passwordForm.confirmPassword)) {
            setFormErrors(prevErrors => ({ ...prevErrors, confirmPassword: "Password must be at least 10 characters long, include 1 capital letter, 1 special character, and 1 numerical digit." }));
        } else {
            setFormErrors(prevErrors => ({ ...prevErrors, confirmPassword: "" }));
        }
        if (validatePassword(passwordForm.newPassword) && validatePassword(passwordForm.confirmPassword) && passwordForm.newPassword === passwordForm.confirmPassword && formErrors.currentPassword === "") {
            const userId = await fetch(`${baseUrl}/getUserIDByToken`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                },
            });
            const id = await userId.json();
            const response = await fetch(`${baseUrl}/changePassword`, {
                method: "PUT",
                headers: {
                    user_id: id.user_id,
                    password: passwordForm.newPassword,
                },
            });
            const data = await response.json();
            if (data.ok || data.success) {
                console.log("success")
            }
            setPasswordForm({
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
            });
            setShowPasswordForm(false);
        }
    };

    async function resetPw() {
        const userId = await fetch(`${baseUrl}/getUserIDByToken`, {
            headers: {
                Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
        });
        const id = await userId.json();
        let url = "/changepass/" + id.user_id
        navigate(url);
    }

    async function getDaysSince() {
        const userId = await fetch(`${baseUrl}/getUserIDByToken`, {
            headers: {
                Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
        });
        const id = await userId.json();
        const response = await fetch(`${baseUrl}/day_since_change`, {
            headers: {
                user_id: id.user_id
            }
        })
        const data = await response.json()
        let days = 0;
        if (typeof data === 'number') {
            days = data;
        } else if (data && typeof data === 'object') {
            days = data.days_since_change || data.days || data.value || 0;
        }

        setPasswordChange(days);
    }

    useEffect(
        () => {
            getDaysSince();
        }, []
    )

    return (
        <>
            <Card>
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 sm:p-6">
                    <h2 className="flex items-center gap-2 sm:gap-3 text-lg sm:text-xl font-semibold">
                        <Key className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                        Password Settings
                    </h2>
                </div>
                <div className="p-4 sm:p-6">
                    {!showPasswordForm ? (
                        <div className="space-y-4 sm:space-y-6">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                <div>
                                    <h3 className="font-medium text-base sm:text-lg">Password Security</h3>
                                    <p className="text-sm text-gray-500">Last changed {passwordChange} days ago</p>
                                </div>
                                <Button
                                    onClick={() => setShowPasswordForm(true)}
                                    fullWidth={window.innerWidth < parseInt(screens.sm)}
                                >
                                    Change Password
                                </Button>
                                <Button
                                    onClick={resetPw}
                                    fullWidth={window.innerWidth < parseInt(screens.sm)}
                                >
                                    Reset Password
                                </Button>
                            </div>
                            <div className="flex items-center gap-2 bg-green-50 p-3 rounded-lg">
                                <Shield className="h-5 w-5 text-green-600 flex-shrink-0" />
                                <span className="text-sm text-green-700">Your password meets security requirements</span>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handlePasswordSubmit} className="space-y-4 sm:space-y-5">
                            {['currentPassword', 'newPassword', 'confirmPassword'].map((field) => (
                                <div key={field} className="space-y-1">
                                    <label className="block text-sm font-medium text-gray-700">
                                        {field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                    </label>
                                    <input
                                        type="password"
                                        name={field}
                                        value={passwordForm[field]}
                                        onChange={handlePasswordChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                                        required
                                    />
                                    {formErrors[field] && (
                                        <span className="text-red-400 text-sm">{String(formErrors[field])}</span>
                                    )}
                                </div>
                            ))}
                            <div className="flex flex-col sm:flex-row gap-3 pt-2">
                                <Button type="submit" fullWidth={window.innerWidth < parseInt(screens.sm)}>
                                    Update Password
                                </Button>
                                <Button
                                    variant="secondary"
                                    onClick={() => setShowPasswordForm(false)}
                                    fullWidth={window.innerWidth < parseInt(screens.sm)}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </form>
                    )}
                </div>
            </Card>

            <Card>
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 sm:p-6">
                    <h2 className="flex items-center gap-2 sm:gap-3 text-lg sm:text-xl font-semibold">
                        <Lock className="h-5 w-5 sm:h-6 sm:w-6 text-gray-600" />
                        Restricted Settings
                    </h2>
                </div>
                <div className="p-4 sm:p-6">
                    <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <p className="text-sm sm:text-base text-yellow-800">
                            These settings require support team verification to modify.
                        </p>
                    </div>
                    <div className="space-y-4 sm:space-y-6 opacity-50">
                        {['Two-Factor Authentication', 'Account Recovery Email'].map((setting) => (
                            <div key={setting} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <span className="font-medium text-sm sm:text-base">{setting}</span>
                                <Switch disabled checked={true} onCheckedChange={() => { }} />
                            </div>
                        ))}
                    </div>
                </div>
            </Card>
        </>
    );
};

const PrivacySettings = () => {
    const [settings, setSettings] = useState({
        profileVisibility: true,
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        loadPrivacySettings();
    }, []);

    const loadPrivacySettings = async () => {
        try {
            const token = localStorage.getItem("access_token");
            const response = await fetch(`${baseUrl}/getPrivacySettings`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            
            if (response.ok) {
                const data = await response.json();
                setSettings({
                    profileVisibility: data.searchable || false,
                });
            }
        } catch (error) {
        } finally {
            setIsLoading(false);
        }
    };

    const handleToggle = async (setting) => {
        const newValue = !settings[setting];
        
        // Optimistic update
        setSettings(prev => ({
            ...prev,
            [setting]: newValue
        }));

        try {
            setIsSaving(true);
            const token = localStorage.getItem("access_token");
            
            const response = await fetch(`${baseUrl}/updatePrivacySettings`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    searchable: newValue
                })
            });

            if (!response.ok) {
                // Revert on error
                setSettings(prev => ({
                    ...prev,
                    [setting]: !newValue
                }));
                throw new Error('Failed to update settings');
            }
        } catch (error) {
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <Card>
                <div className="p-6 text-center">Loading privacy settings...</div>
            </Card>
        );
    }

    return (
        <Card>
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 sm:p-6">
                <h2 className="flex items-center gap-2 sm:gap-3 text-lg sm:text-xl font-semibold">
                    <User className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
                    Privacy Controls
                </h2>
            </div>
            <div className="p-4 sm:p-6">
                <div className="space-y-4 sm:space-y-6">
                    <div className="flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 rounded-lg transition-colors gap-4">
                        <div className="flex items-start sm:items-center gap-2 sm:gap-3">
                            <User className="h-5 w-5 text-gray-500 flex-shrink-0 mt-1 sm:mt-0" />
                            <div>
                                <h3 className="font-medium text-sm sm:text-base">Profile Visibility</h3>
                                <p className="text-xs sm:text-sm text-gray-500">Allow others to search and view your profile</p>
                            </div>
                        </div>
                        <Tooltip content="Make your profile searchable by other users" position="left">
                            <Info className="h-5 w-5 text-gray-400 cursor-pointer" />
                        </Tooltip>
                        <Switch
                            checked={settings.profileVisibility}
                            onCheckedChange={() => handleToggle('profileVisibility')}
                            disabled={isSaving}
                        />
                    </div>
                </div>
            </div>
        </Card>
    );
};

const NotificationSettings = () => {
    const [notifications, setNotifications] = useState({
        emailNotifications: true,
        smsNotifications: false,
        pushNotifications: true,
    });

    const handleNotificationToggle = (notification) => {
        setNotifications(prev => ({
            ...prev,
            [notification]: !prev[notification]
        }));
    };

    return (
        <Card>
            <div className="bg-gradient-to-r from-green-50 to-teal-50 p-4 sm:p-6">
                <h2 className="flex items-center gap-2 sm:gap-3 text-lg sm:text-xl font-semibold">
                    <Bell className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
                    Notification Settings
                </h2>
            </div>
            <div className="p-4 sm:p-6">
                <div className="space-y-4 sm:space-y-6">
                    {[
                        { key: 'emailNotifications', label: 'Email Notifications', desc: 'Receive notifications via email' },
                        { key: 'smsNotifications', label: 'SMS Notifications', desc: 'Receive notifications via SMS' },
                        { key: 'pushNotifications', label: 'Push Notifications', desc: 'Receive push notifications on your device' }
                    ].map(({ key, label, desc }) => (
                        <div key={key} className="flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 rounded-lg transition-colors gap-4">
                            <div className="flex items-start sm:items-center gap-2 sm:gap-3">
                                <Bell className="h-5 w-5 text-gray-500 flex-shrink-0 mt-1 sm:mt-0" />
                                <div>
                                    <h3 className="font-medium text-sm sm:text-base">{label}</h3>
                                    <p className="text-xs sm:text-sm text-gray-500">{desc}</p>
                                </div>
                            </div>
                            <Tooltip content={desc} position="left">
                                <Info className="h-5 w-5 text-gray-400 cursor-pointer" />
                            </Tooltip>
                            <Switch
                                checked={notifications[key]}
                                onCheckedChange={() => handleNotificationToggle(key)}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </Card>
    );
};

const SettingsPage = () => {
    const [activeTab, setActiveTab] = useState('security');

    return (
        <>
            <SEO
              title="Privacy Settings"
              description="Manage your ProducerBuddy privacy settings, data sharing preferences, and account security options."
              url="https://producerbuddy.pilomation.com/privacy-settings"
              robots="noindex, nofollow"
            />
            <SubNav />
            <div className="min-h-screen bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
                    <div className="mb-6 sm:mb-8">
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Account Settings</h1>
                        <p className="text-sm sm:text-base text-gray-500 mt-2">Manage your account security and privacy preferences</p>
                    </div>

                    <div className="mb-6">
                        <div className="flex space-x-1 bg-white rounded-lg p-1 max-w-xs sm:max-w-sm border">
                            {['security', 'privacy', 'notifications'].map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`flex-1 p-2 rounded-md font-medium text-sm capitalize transition-colors
                                        ${activeTab === tab
                                            ? 'bg-blue-100 text-blue-700'
                                            : 'text-gray-600 hover:bg-gray-50'
                                        }`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-6">
                        {activeTab === 'security' && <SecuritySettings />}
                        {activeTab === 'privacy' && <PrivacySettings />}
                        {activeTab === 'notifications' && <NotificationSettings />}
                    </div>
                </div>
            </div>
        </>
    );
};

export default SettingsPage;
